// @ts-check
// Controles do painel de configuração de widget: seletor com busca, alternador, segmentado e texto.
import { html } from 'htm/preact';
import { useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { Toggle } from '../../ui/feedback.js';
import {
  Dropdown,
  DropdownMenuItems,
  DropdownMenuSearchInput,
  DropdownMenuSectionLabel,
  DropdownMenuSeparator,
  MenuItem,
  useKeyboardSelection,
} from '../../ui/dropdown.js';
import { normalizeSearchText } from '../../lib/utils.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {{ value: string, label: string, icon?: string | null, group?: string, contextualText?: string }} SelectOption
 */

/**
 * @param {{ label: string, icon?: string, hint?: string, children: any }} props
 */
export function EditorField({ label, icon, hint, children }) {
  return html`<div class="form-field widget-editor-field">
    <span class="form-field-label">${icon ? html`<${Icon} name=${icon} size=${14} />` : null}<span>${label}</span></span>
    ${children}
    ${hint ? html`<span class="widget-editor-hint">${hint}</span>` : null}
  </div>`;
}

/**
 * @param {{ title: string, children: any }} props
 */
export function EditorSection({ title, children }) {
  return html`<section class="widget-editor-section">
    <h4 class="widget-editor-section-title">${title}</h4>
    <div class="widget-editor-section-body">${children}</div>
  </section>`;
}

/**
 * @param {{ value: string | null | undefined, options: SelectOption[], onChange: (value: string | null) => void, placeholder?: string,
 *   searchable?: boolean, allowEmpty?: boolean, emptyLabel?: string, ariaLabel?: string, disabled?: boolean }} props
 */
export function SelectControl({ value, options, onChange, placeholder, searchable = false, allowEmpty = false, emptyLabel, ariaLabel, disabled = false }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const selected = options.find((option) => option.value === value);
  const matching = options.filter((option) => normalizeSearchText(option.label).includes(normalizeSearchText(searchText)));
  /** @type {(SelectOption | null)[]} */
  const items = [...(allowEmpty && !searchText ? [null] : []), ...matching];
  const close = () => {
    setIsOpen(false);
    setSearchText('');
  };
  const choose = (/** @type {SelectOption | null} */ option) => {
    onChange(option ? option.value : null);
    close();
  };
  const { focusedIndex, setFocusedIndex, handleKeyDown } = useKeyboardSelection(items.length, (index) => choose(items[index]));
  let previousGroup = '';
  return html`<span class="select-control-wrapper">
    <button
      ref=${anchorRef}
      type="button"
      class="select-control"
      aria-haspopup="listbox"
      aria-expanded=${isOpen ? 'true' : 'false'}
      aria-label=${ariaLabel}
      disabled=${disabled}
      onClick=${() => (isOpen ? close() : setIsOpen(true))}
    >
      <span class="select-control-value">
        ${selected?.icon ? html`<${Icon} name=${selected.icon} size=${14} />` : null}
        ${selected
          ? html`<span class="select-control-text">${selected.label}</span>`
          : allowEmpty && !value
            ? html`<span class="select-control-text">${emptyLabel ?? t('dashboards.editor.none')}</span>`
            : html`<span class="select-control-placeholder">${value ? value : placeholder ?? t('dashboards.editor.choose')}</span>`}
      </span>
      <${Icon} name="IconChevronDown" size=${14} />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${close} width="anchor" maxHeight=${320}>
      ${searchable
        ? html`<${DropdownMenuSearchInput} value=${searchText} placeholder=${t('common.search')}
            onChange=${(text) => { setSearchText(text); setFocusedIndex(0); }} onKeyDown=${handleKeyDown} />
          <${DropdownMenuSeparator} />`
        : null}
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${items.length === 0 ? html`<div class="menu-item-empty">${t('common.noResults')}</div>` : null}
        ${items.map((option, index) => {
          if (option === null) {
            return html`<${MenuItem} key="__empty" icon="IconCircleOff" accent="placeholder" text=${emptyLabel ?? t('dashboards.editor.none')}
              selected=${!value} focused=${focusedIndex === index} onClick=${() => choose(null)} />`;
          }
          const groupLabel = option.group && option.group !== previousGroup ? option.group : null;
          previousGroup = option.group ?? previousGroup;
          return html`
            ${groupLabel ? html`<${DropdownMenuSectionLabel} key=${`group-${groupLabel}`} label=${groupLabel} />` : null}
            <${MenuItem} key=${option.value} icon=${option.icon ?? null} text=${option.label} contextualText=${option.contextualText}
              selected=${option.value === value} focused=${focusedIndex === index} onClick=${() => choose(option)} />
          `;
        })}
      <//>
    <//>
  </span>`;
}

/**
 * @param {{ label: string, icon?: string, value: boolean, onChange: (value: boolean) => void }} props
 */
export function ToggleRow({ label, icon, value, onChange }) {
  return html`<label class="widget-editor-toggle">
    <span class="form-field-label">${icon ? html`<${Icon} name=${icon} size=${14} />` : null}<span>${label}</span></span>
    <${Toggle} value=${value} ariaLabel=${label} size="small" onChange=${onChange} />
  </label>`;
}

/**
 * @param {{ value: string, options: { value: string, label: string, icon?: string }[], onChange: (value: string) => void }} props
 */
export function SegmentedControl({ value, options, onChange }) {
  return html`<div class="segmented-control" role="tablist">
    ${options.map((option) => html`<button
      type="button"
      key=${option.value}
      role="tab"
      class="segmented-item"
      aria-selected=${value === option.value ? 'true' : 'false'}
      data-active=${value === option.value ? 'true' : undefined}
      onClick=${() => onChange(option.value)}
    >${option.icon ? html`<${Icon} name=${option.icon} size=${14} />` : null}${option.label}</button>`)}
  </div>`;
}
